import { Configuration } from '@/model/configuration/Configuration'
import { reactive } from 'vue'
import useAppState, { Languages, ViewType } from '@/composables/ui/ui'
import jwtDecode from 'jwt-decode'
import { JwtToken } from '@/model/data-object/JwtToken'

const configuration = reactive<Configuration>(new Configuration())

export const ROOT_ELEMENT = '#anzu-forum-widget'
export const JWT_PUBLIC_COOKIES = ['anz_ppp', 'ppp']
export const DISABLE_WIDGET_FORM_ID = 1
export const CROSSROADS_ELEMENT = '#anzu-forum-widget-profile'

export default function useConfiguration() {
  const resolveWidgetConfiguration = () => {
    const el: Element | null = document.querySelector(ROOT_ELEMENT)
    const crossroadsEl: Element | null = document.querySelector(
      CROSSROADS_ELEMENT
    )
    if (el instanceof HTMLElement) {
      if (el.dataset.topicId) {
        configuration.widget.topicId = parseInt(el.dataset.topicId)
      } else {
        configuration.widget.topicId = 0
      }
      if (el.dataset.legacyTopicId) {
        configuration.widget.legacyTopicId = parseInt(el.dataset.legacyTopicId)
      }
      if (el.dataset.profileId) {
        configuration.widget.userId = parseInt(el.dataset.profileId)
      } else {
        configuration.widget.userId = 0
      }
      if (el.dataset.advertSlotPrefixName) {
        configuration.widget.advertSlotPrefixName =
          el.dataset.advertSlotPrefixName
      }
      if (el.dataset.loginBannerPrefixId) {
        configuration.widget.loginBannerPrefixId =
          el.dataset.loginBannerPrefixId
      }
      if (el.dataset.currentUrl) {
        configuration.widget.currentUrl = el.dataset.currentUrl
      }
      if (el.dataset.loginUrl) {
        configuration.widget.loginUrl = el.dataset.loginUrl
      }
      if (el.dataset.advertSlotPrefixId) {
        configuration.widget.advertSlotPrefixId = el.dataset.advertSlotPrefixId
      }
      if (el.dataset.scrollToPostOffsetCallback) {
        configuration.widget.scrollToPostOffsetCallback =
          el.dataset.scrollToPostOffsetCallback
      }
      configuration.widget.advertDisabled =
        typeof el.dataset.advertDisabled !== 'undefined'
      configuration.widget.analyticsEventDisabled =
        typeof el.dataset.analyticsEventDisabled !== 'undefined'
      configuration.widget.subscriptionLinkDisabled =
        typeof el.dataset.subscriptionLinkDisabled !== 'undefined'
      configuration.widget.loginBannerDisabled =
        typeof el.dataset.loginBannerDisabled !== 'undefined'
      configuration.widget.showProfileCrossroads =
        typeof el.dataset.showProfileCrossroads !== 'undefined' &&
        crossroadsEl !== null
      configuration.widget.onlyTopNewest =
        typeof el.dataset.onlyTopNewest !== 'undefined'
      configuration.widget.loginBannerFallbackDisabled =
        typeof el.dataset.loginBannerFallbackDisabled !== 'undefined'
      configuration.widget.darkModeEnabled =
        typeof el.dataset.darkModeEnabled !== 'undefined'
      configuration.widget.profileUserEdit =
        typeof el.dataset.profileUserEdit !== 'undefined'
      configuration.widget.profileEmailEdit =
        typeof el.dataset.profileEmailEdit !== 'undefined'
      configuration.widget.postUserLinkDisabled =
        typeof el.dataset.postUserLinkDisabled !== 'undefined'
      configuration.widget.postDateLinkDisabled =
        typeof el.dataset.postDateLinkDisabled !== 'undefined'
      configuration.widget.widgetAutoloadDisabled =
        typeof el.dataset.widgetAutoloadDisabled !== 'undefined'
    }
  }

  const resolveViewType = () => {
    const { viewType } = useAppState()

    if (configuration.extSysConf.isAllDisabled()) {
      viewType.value = ViewType.WIDGET_DISABLED
    } else if (configuration.widget.onlyTopNewest) {
      viewType.value = ViewType.TOP_NEWEST
    } else if (configuration.widget.profileEmailEdit) {
      viewType.value = ViewType.PROFILE_EMAIL_EDIT
    } else if (configuration.widget.profileUserEdit) {
      viewType.value = ViewType.PROFILE_USER_EDIT
    } else if (configuration.widget.userId > 0) {
      viewType.value = ViewType.PROFILE
    } else if (configuration.widget.topicId > 0) {
      viewType.value = ViewType.DISCUSSION
    }
  }

  const resolveLanguage = () => {
    const { language } = useAppState()

    if (document.documentElement.lang in Languages) {
      language.value = document.documentElement.lang as Languages
    }
  }

  const setVersion = (version: string) => {
    configuration.appVersion = version
  }

  const resolveExtSysConf = () => {
    const customData = window.anzu?.forum?.widgetParams || []
    if (typeof customData === 'object') {
      const disableConfig = customData.find(
        (config: any) =>
          config.formId && config.formId === DISABLE_WIDGET_FORM_ID
      )
      if (!disableConfig) {
        return
      }

      configuration.extSysConf.disableText =
        disableConfig.disableText || configuration.extSysConf.disableText
      configuration.extSysConf.disableAllActions =
        disableConfig.disableAllActions ||
        configuration.extSysConf.disableAllActions
      configuration.extSysConf.disabledWriteActions =
        disableConfig.disabledWriteActions ||
        configuration.extSysConf.disabledWriteActions

      if (disableConfig.disableUntil) {
        configuration.extSysConf.disableUntil = new Date(
          disableConfig.disableUntil
        )
      }
      if (disableConfig.disableFrom) {
        configuration.extSysConf.disableFrom = new Date(
          disableConfig.disableFrom
        )
      }
    }
  }

  const getFirstCookie = (toFind: Array<string>): string | undefined => {
    const ca = document.cookie.split(';')
    for (let i = 0; i < ca.length; i++) {
      const parts = ca[i].split('=')
      if (parts.length > 1 && toFind.includes(parts[0].trim())) {
        return parts[1]
      }
    }
    return undefined
  }

  const getJwt = (): JwtToken | undefined => {
    const jwtCookie = getFirstCookie(JWT_PUBLIC_COOKIES)

    if (jwtCookie) {
      const decoded = jwtDecode<Record<string, any>>(jwtCookie)
      return JwtToken.fromObject(decoded)
    }

    return undefined
  }

  return {
    configuration,
    resolveExtSysConf,
    resolveWidgetConfiguration,
    resolveViewType,
    resolveLanguage,
    getJwt,
    setVersion,
  }
}
